import { BaseModel } from '../../../global/models';

export interface ShippingSettings extends BaseModel {
  freeWeightLimit: number;
  weightPrices: WeightPrice;
  priceRanges: ShippingPriceRange[];
  deliveryDaysRanges: DeliveryDaysRange;
  zoneWeightPrices: ZoneWeightPrice[];
  shippingTypeRestrictions: ShippingTypeRestrictions;
  zoneFreeCases: ZoneFreeCase[];
  zoneShippingInfo: ZoneShippingInfo[];
  available: ShippingType[];
  extra_economic: number;
  extra_24: number;
  extra_24_48?: number;
  extra_urgent: number;

  availableCouriersForPoints: AvailableCouriersForPoints;
  businessAndExpensiveException: BusinessAndExpensiveException;
  envelopeByCourier: { [courier: string]: { [serviceType: string]: EnvelopeImage; }; };
  highSeason: HighSeason;
  lastTrackingUpdate: Date;
  scheduleByCourier: CourierSchedule;
}

/**
 * Precio del envío por rango de peso (caso general)
 * los dos primeros valores son el rango de peso (en kg) => [cerrado, abierto)
 * el tercer valor es el precio (en euros)
 * extra es el precio por Kg que se debe cobrar si excede el rango (en euros)
 */
interface WeightPrice {
  range: RangePrice[];
  extra: number; // (en euros) precio por kilo extra si se está cobrando el envío
  freeExtra: number;  // (en euros) precio por kilo extra si NO se está cobrando el envío, a partir de FREEWEIGHTLIMIT kilos
}

interface ShippingPriceRange {
  minLimit: number;
  maxLimit: number;
  price: number;
}

type RangePrice = [number, number, number]; //low hight price

interface ZoneWeightPrice extends WeightPrice {
  zone: Array<string>; //postalcodes
}

interface ZoneFreeCase {
  zone: Array<string>,
  amount: number;
}

interface ZoneShippingInfo {
  zone: Array<string>,
  info: string; //HTML
}

export enum ShippingType {
  ECONOMIC = 'economic',
  STANDARD = 'standard', // 24 h (+1€)
  STANDARD_24 = 'standard_24', // 24 h (+1€)
  URGENT = 'urgent', // next day 14 h (+4€)
  PICKUP = 'pickup'
}

export interface AvailableCouriersForPoints {
  [courierName: string]: CourierConfig;
}

export interface CourierConfig {
  enabled: boolean;
  maxWeight?: MaxWeight;
  shippingTypes?: AvailableShippingTypes;
}

interface MaxWeight {
  enabled: boolean;
  value?: number;
}

interface AvailableShippingTypes {
  standard_24?: boolean;
  urgent?: boolean;
}


interface DeliveryDaysRange {
  exceptions: DeliveryDaysException[];
  expensiveThreshold: number;
  shippingTypes: { [shippingType in ShippingType]?: DaysRange };
}

interface DeliveryDaysException {
  annotation: string;
  days: number;
  type: string;
  value: string[];
}

interface BusinessAndExpensiveException {
  courier: string;
  enabled: boolean;
  minPrice: number;
}

interface DaysRange {
  maxDays: number;
  maxDaysExpensive: number;
  minDays: number;
  minDaysExpensive: number;
}

interface EnvelopeImage {
  imageUrl: string;
}

interface HighSeason {
  active: boolean;
  message: string;
}

interface CourierSchedule {
  [courier: string]: Schedule[];
}

interface Schedule {
  enabled: boolean;
  from: string;
  to: string;
}

interface ShippingTypeRestrictions {
  [shippingType: string]: Restriction[];
}

interface Restriction {
  provinceIds: string[];
  show: boolean;
}


// **** MODELOS PARA SUBCOLECCIONES ****

export interface ShippingBalancer<TService = string> extends BaseModel {
  balanceUpdatedAt?: Date;
  balancer?: Record<string, number>;
  counter?: Record<string, number>;
  service?: Record<string, TService>;
}

export interface ShippingBalancerByZone extends ShippingBalancer<ShippingBalancerCourierServices> {
  description?: string;
  postalCodePrefixesForZone?: string[];
  deliveryDeadlines?: Record<string, ShippingBalancerCourierDeadlines>;
  priority?: boolean;
}

export interface ShippingBalancerCourierServices {
  economic?: string;
  standard?: string;
  standard_24?: string;
  urgent?: string;

  [shippingType: string]: string | undefined;
}

export interface ShippingBalancerCourierDeadlines {
  economic?: ShippingDeadline;
  standard?: ShippingDeadline;
  standard_24?: ShippingDeadline;
  urgent?: ShippingDeadline;

  [shippingType: string]: ShippingDeadline | undefined;
}

export interface ShippingDeadline {
  maxDays?: number;
  maxHour?: string;
}


export interface ShippingBalancerByShippingType extends ShippingBalancer<string> {
  enforces?: ShippingBalancerEnforce[];
}

export interface ShippingBalancerEnforce {
  postalCodePrefixes?: Array<string | number>;
  pairs?: ShippingBalancerEnforcePair[];
}

export interface ShippingBalancerEnforcePair {
  shippingCourier?: string;
  shippingCourierService?: string | null;
}
